/**
 * @file toast-notification.js
 * @description Global notification engine for managing dynamic toast alerts.
 * Communicates via decoupled CustomEvents decoupled from Shopify's template rendering.
 *
 * ARCHITECTURAL SPECIFICATIONS
 *
 * * ASSUMPTIONS:
 * 1. DOM Availability: It is assumed that a the snippet "toast-notification.liquid" is rendered somewhere on the page.
 * 2. Event Payload Structure: Fired events must supply data inside the native
 * `event.detail` object matching options: {title, message, type, duration, trackingId}.
 * 3. Execution Context: Runs strictly in a client-side browser environment where
 * the global `window` and `document` objects are fully initialized.
 * * CONSTRAINTS:
 * 1. Single Instance Rule: Only one instance of `ToastNotification` should be
 * instantiated per page. Multiple instances will cause duplicated event handlers.
 * 2. Performance Bound: Max limit of simultaneously visible toasts is visually
 * constrained by screen height, though the DOM allows infinite queuing.
 * 3. Text Length: Visual elements assume short, actionable strings. Extremely
 * long text bodies will be safely wrapped but may degrade the UI layout.
 * 4. Dependency Constraint: Relies on modern ES6+ browser features (Arrow functions,
 * Classes, Template Literals) and CSS Transition support.
 * * ============================================================================
 */
class ToastNotification {
  constructor() {
    this.container = document.getElementById('toast-container');
    this.initEventListeners();
  }

  /**
   * Binds global window listeners.
   */
  initEventListeners() {
    window.addEventListener('shopify:toast', (event) => {
      // Edge Case: Guard against malformed or missing detail payloads
      if (event && event.detail) {
        this.render(event.detail);
      }
    });
  }

  /**
   * Generates and mounts toast node.
   * @param {Object} options
   */
  render(options) {
    // Edge Case: Lazy evaluation if snippet container wasn't loaded during constructor call
    if (!this.container) {
      this.container = document.getElementById('toast-container');
      if (!this.container) {
        console.warn(
          'ToastNotification: Target wrapper "#toast-container" was not found in the DOM.'
        );
        return;
      }
    }

    const { title, message, type = 'error', duration = 6000, trackingId } = options;

    const toast = document.createElement('div');
    toast.className = `toast-notification toast--${type}`;
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');

    // Constraint/Edge Case Helper: Strict escaping algorithm to counter malicious text strings
    const escapeHTML = (str) => {
      if (typeof str !== 'string') return '';
      return str.replace(
        /[&<>'"]/g,
        (tag) =>
          ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[tag] || tag)
      );
    };

    let metaHTML = '';
    if (trackingId) {
      metaHTML = `<div class="toast-meta">Tracking ID: ${escapeHTML(trackingId)}</div>`;
    }

    toast.innerHTML = `
      <div class="toast-title">${escapeHTML(title)}</div>
      <div class="toast-message">${escapeHTML(message)}</div>
      ${metaHTML}
    `;

    this.container.appendChild(toast);

    // Forces a reflow layout calculation before adding the transition animation class
    setTimeout(() => toast.classList.add('toast--visible'), 10);

    // Setup auto-dismissal
    let dismissTimeout = setTimeout(() => this.dismiss(toast), duration);

    // Edge Case: Allow instant dismissal by click, bypassing and canceling the active timeout loop
    toast.addEventListener('click', () => {
      if (dismissTimeout) {
        clearTimeout(dismissTimeout);
        dismissTimeout = null;
      }
      this.dismiss(toast);
    });
  }

  /**
   * Gracefully slides out and sweeps nodes from memory.
   * @param {HTMLElement} toast
   */
  dismiss(toast) {
    if (!toast) return;

    toast.classList.remove('toast--visible');

    // Edge Case: Ensure node is physically purged from DOM only after CSS animations end
    const handleTransitionEnd = () => {
      toast.removeEventListener('transitionend', handleTransitionEnd);
      toast.remove();
    };

    toast.addEventListener('transitionend', handleTransitionEnd);
  }
}

// Global initialization
new ToastNotification();
